import { useEffect, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, GlassCard, Input, StyledSelect } from '@jumpstone/ui-react';
import { usePayrollSettings } from '../../data/use_payroll_settings';
import {
  buildPayrollTabPatch,
  derivePayrollTabFields,
  isEmptyPatch,
  isValidOvertimeDivisor,
  parseIntegerInput,
} from '../../data/wagepro_store_payroll_settings';
import type {
  PayFrequency,
  ProrationMethod,
  RoundingRule,
} from '../../data/types';
import { PayrollSettingsStateGate } from '../payroll/payroll_settings_state_gate';

/** Matches `settings_screen.tsx`'s Organisation-form trigger shell. */
const SELECT_TRIGGER_CLASSES =
  'nv-input flex h-9 max-w-[260px] cursor-pointer items-center justify-between gap-2';

const PAY_FREQUENCY_OPTIONS: Array<{ value: PayFrequency; label_key: string }> =
  [
    { value: 'monthly', label_key: 'pay_frequency.monthly' },
    { value: 'fortnightly', label_key: 'pay_frequency.fortnightly' },
    { value: 'weekly', label_key: 'pay_frequency.weekly' },
  ];

const ROUNDING_RULE_OPTIONS: Array<{ value: RoundingRule; label_key: string }> =
  [
    { value: 'nearest_unit', label_key: 'rounding_rule.nearest_unit' },
    { value: 'nearest_ten', label_key: 'rounding_rule.nearest_ten' },
  ];

const PRORATION_METHOD_OPTIONS: Array<{
  value: ProrationMethod;
  label_key: string;
}> = [
  { value: 'calendar_day', label_key: 'proration_method.calendar_day' },
  { value: 'working_day', label_key: 'proration_method.working_day' },
];

/**
 * Settings screen's Payroll tab (#22730) — default pay frequency, rounding
 * rule, proration method, and overtime hours divisor, real and editable via
 * `usePayrollSettings()` (`payroll.getPayrollSettings`/
 * `updatePayrollSettings`, #22729). Replaces the permanently-mock
 * `MOCK_PAYROLL_SETTINGS` this tab used to render unconditionally (before
 * this pass, `settings_screen.tsx` ignored its own `tab` prop entirely and
 * always showed the Organisation form regardless of which Settings tab was
 * selected).
 */
export function SettingsPayrollSection(): React.JSX.Element {
  const { t } = useTranslation('wp');
  const payrollSettings = usePayrollSettings();
  const [defaultPayFrequency, setDefaultPayFrequency] =
    useState<PayFrequency>('monthly');
  const [roundingRule, setRoundingRule] =
    useState<RoundingRule>('nearest_unit');
  const [prorationMethod, setProrationMethod] =
    useState<ProrationMethod>('calendar_day');
  const [overtimeDivisorText, setOvertimeDivisorText] = useState('240');
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    if (payrollSettings.state.status !== 'ready') return;
    const fields = derivePayrollTabFields(payrollSettings.state.settings);
    setDefaultPayFrequency(fields.default_pay_frequency);
    setRoundingRule(fields.rounding_rule);
    setProrationMethod(fields.proration_method);
    setOvertimeDivisorText(String(fields.overtime_hours_divisor));
  }, [payrollSettings.state]);

  if (payrollSettings.state.status !== 'ready') {
    return (
      <PayrollSettingsStateGate
        state={payrollSettings.state}
        retry={payrollSettings.retry}
        t={t}
      />
    );
  }

  // Captured as its own const (not re-read as `payrollSettings.state.settings`
  // inside `handleSave` below) -- TypeScript's narrowing of the
  // `status !== 'ready'` guard above does not carry through a member
  // expression into a nested function body, only through a plain local
  // identifier.
  const seededSettings = payrollSettings.state.settings;
  const canWrite = payrollSettings.save !== undefined;
  const overtimeDivisor = parseIntegerInput(overtimeDivisorText);
  const overtimeDivisorValid =
    overtimeDivisor !== null && isValidOvertimeDivisor(overtimeDivisor);

  async function handleSave(): Promise<void> {
    if (!payrollSettings.save || !overtimeDivisorValid) return;
    const patch = buildPayrollTabPatch(seededSettings, {
      defaultPayFrequency,
      roundingRule,
      prorationMethod,
      // Non-null: gated on `overtimeDivisorValid` above.
      overtimeHoursDivisor: overtimeDivisor,
    });
    if (isEmptyPatch(patch)) {
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
      }, 2000);
      return;
    }
    setIsSaving(true);
    setSaveFailed(false);
    const result = await payrollSettings.save(patch);
    setIsSaving(false);
    if (result.outcome === 'success') {
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
      }, 2000);
    } else {
      setSaveFailed(true);
    }
  }

  function handleCancel(): void {
    if (payrollSettings.settings) {
      const fields = derivePayrollTabFields(payrollSettings.settings);
      setDefaultPayFrequency(fields.default_pay_frequency);
      setRoundingRule(fields.rounding_rule);
      setProrationMethod(fields.proration_method);
      setOvertimeDivisorText(String(fields.overtime_hours_divisor));
    }
    setSaveFailed(false);
  }

  return (
    <GlassCard className="rounded-2xl p-5">
      <div className="grid grid-cols-1 gap-5">
        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] items-start sm:items-center gap-1.5 sm:gap-4">
          <label className="nv-label">
            {t('settings.default_pay_frequency')}
          </label>
          <StyledSelect
            value={defaultPayFrequency}
            onChange={setDefaultPayFrequency}
            options={PAY_FREQUENCY_OPTIONS.map((option) => ({
              value: option.value,
              label: t(option.label_key),
            }))}
            ariaLabel={t('settings.default_pay_frequency')}
            triggerClassName={SELECT_TRIGGER_CLASSES}
            disabled={!canWrite}
          />
        </div>

        <div className="border-t border-[color:var(--line)]" />

        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] items-start sm:items-center gap-1.5 sm:gap-4">
          <label className="nv-label">{t('settings.rounding_rule')}</label>
          <StyledSelect
            value={roundingRule}
            onChange={setRoundingRule}
            options={ROUNDING_RULE_OPTIONS.map((option) => ({
              value: option.value,
              label: t(option.label_key),
            }))}
            ariaLabel={t('settings.rounding_rule')}
            triggerClassName={SELECT_TRIGGER_CLASSES}
            disabled={!canWrite}
          />
        </div>

        <div className="border-t border-[color:var(--line)]" />

        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] items-start sm:items-center gap-1.5 sm:gap-4">
          <label className="nv-label">{t('settings.proration_method')}</label>
          <StyledSelect
            value={prorationMethod}
            onChange={setProrationMethod}
            options={PRORATION_METHOD_OPTIONS.map((option) => ({
              value: option.value,
              label: t(option.label_key),
            }))}
            ariaLabel={t('settings.proration_method')}
            triggerClassName={SELECT_TRIGGER_CLASSES}
            disabled={!canWrite}
          />
        </div>

        <div className="border-t border-[color:var(--line)]" />

        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] items-start sm:items-center gap-1.5 sm:gap-4">
          <label className="nv-label">{t('settings.overtime_basis')}</label>
          <div className="max-w-[160px]">
            <Input
              type="text"
              inputMode="numeric"
              value={overtimeDivisorText}
              disabled={!canWrite}
              error={!overtimeDivisorValid}
              onChange={(e) => {
                setOvertimeDivisorText(e.target.value);
              }}
            />
            {overtimeDivisorValid ? (
              <p className="nv-help">
                {t('settings.overtime_basis_value', { hours: overtimeDivisor })}
              </p>
            ) : (
              <p className="nv-help is-error">
                {t('settings.overtime_divisor_invalid')}
              </p>
            )}
          </div>
        </div>
      </div>

      {canWrite && (
        <div className="flex items-center justify-end gap-3 mt-4">
          {saveFailed && (
            <p className="nv-help is-error mt-0">
              {t('payroll_settings.save_error')}
            </p>
          )}
          <Button variant="ghost" onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleSave()}
            loading={isSaving}
            disabled={!overtimeDivisorValid}
          >
            {saved ? t('settings.saved') : t('settings.save')}
          </Button>
        </div>
      )}
    </GlassCard>
  );
}
